import React, { useState, useEffect } from 'react';
import { useLocation, Outlet } from 'react-router-dom';
import { ArrowUp } from 'lucide-react';
import CollegeNavbar from '../components/CollegeNavbar';
import Sidebar from '../components/Sidebar';

const CollegeLayout = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [isVisible, setIsVisible] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [collegeInfo, setCollegeInfo] = useState(null);
  const location = useLocation();

  // Reset scroll position on route change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  // Fetch college info
  useEffect(() => {
    const fetchCollegeInfo = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BASE_URL}/api/college/profile`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        if (response.ok) {
          const data = await response.json();
          setCollegeInfo(data);
        }
      } catch (error) {
        console.error('Error fetching college info:', error);
      } finally {
        setTimeout(() => setIsLoading(false), 1000);
      }
    };

    fetchCollegeInfo();
  }, []);

  // Handle scroll to top visibility
  useEffect(() => {
    const handleScroll = () => {
      setIsVisible(window.pageYOffset > 300);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Handle sidebar responsiveness
  useEffect(() => {
    const handleResize = () => {
      setIsSidebarOpen(window.innerWidth >= 1024);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  if (isLoading) {
    return (
      <div className="fixed inset-0 bg-white flex items-center justify-center">
        <div className="relative">
          <div className="relative h-32 w-32 md:h-40 md:w-40 mx-auto">
            {collegeInfo?.logo ? (
              <img 
                src={collegeInfo.logo}
                alt={collegeInfo.name}
                className="w-full h-full object-contain"
              />
            ) : (
              <div className="w-full h-full rounded-2xl bg-gray-50 flex items-center justify-center">
                <span className="text-4xl md:text-5xl font-bold text-gray-400">
                  {collegeInfo?.name?.[0] || 'C'}
                </span>
              </div>
            )}
          </div>
          
          <div className="absolute -inset-4">
            <div className="w-full h-full rounded-full border-2 border-gray-800 border-t-transparent animate-spin" />
          </div>

          <div className="absolute -bottom-12 left-1/2 transform -translate-x-1/2 w-max">
            <p className="text-sm text-gray-500 animate-pulse">
              {collegeInfo?.name || 'Loading...'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50/50">
      <CollegeNavbar 
        isSidebarOpen={isSidebarOpen}
        setIsSidebarOpen={setIsSidebarOpen}
        collegeInfo={collegeInfo}
      />
      
      <Sidebar 
        isOpen={isSidebarOpen}
        setIsOpen={setIsSidebarOpen}
        collegeInfo={collegeInfo}
      />
      
      <main className={`transition-all duration-300 ease-out 
        ${isSidebarOpen ? 'lg:pl-72' : 'lg:pl-24'} pt-16`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Outlet />
        </div>
      </main>

      {/* Improved Scroll to Top Button */}
      <button
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        className={`fixed bottom-8 right-8 p-3 bg-white rounded-full shadow-lg
          hover:shadow-xl transition-all duration-300 z-50 group
          ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-16 opacity-0'}`}
      >
        <div className="absolute inset-0 bg-blue-50 rounded-full opacity-0 
          group-hover:opacity-100 transition-opacity duration-300" />
        <ArrowUp className="w-5 h-5 text-blue-600 relative z-10" />
      </button>
    </div>
  );
};

export default CollegeLayout;